import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Camera, Dumbbell, X, Flame } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/api';

const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const DAYS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const CalendarPage = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [calendarData, setCalendarData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCalendar = useCallback(async () => {
    if (!calendarData) setLoading(true);
    try {
      const res = await axios.get(`${API}/user/calendar?month=${month}&year=${year}`);
      setCalendarData(res.data);
      try { localStorage.setItem(`ferraz_cal_${year}_${month}`, JSON.stringify(res.data)); } catch (e) { console.warn('[CalendarPage] non-critical failure', e); }
    } catch {
      // Try cache
      try { const c = JSON.parse(localStorage.getItem(`ferraz_cal_${year}_${month}`)); if (c) setCalendarData(c); } catch (e) { console.warn('[CalendarPage] non-critical failure', e); }
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };

  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-black p-4 pb-24" data-testid="calendar-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <CalendarIcon className="w-6 h-6 text-neon-lime" />
          <h1 className="font-primary font-bold text-2xl text-white uppercase tracking-tight">Calendário</h1>
        </div>
        <p className="text-zinc-500 text-sm">Seu histórico de treinos</p>
      </motion.div>

      {/* Stats */}
      {calendarData && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-zinc-950 border border-white/5 rounded-xl p-3 text-center">
            <Dumbbell className="w-5 h-5 text-neon-purple mx-auto mb-1" />
            <p className="text-xl font-bold text-white">{calendarData.total_workouts}</p>
            <p className="text-xs text-zinc-500">Treinos</p>
          </div>
          <div className="bg-zinc-950 border border-white/5 rounded-xl p-3 text-center">
            <Camera className="w-5 h-5 text-neon-lime mx-auto mb-1" />
            <p className="text-xl font-bold text-white">{calendarData.total_photos}</p>
            <p className="text-xs text-zinc-500">Fotos</p>
          </div>
        </motion.div>
      )}

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4 px-1">
        <button onClick={prevMonth} className="w-9 h-9 rounded-full bg-zinc-900 border border-white/5 flex items-center justify-center hover:bg-zinc-800 active:scale-90 transition-all">
          <ChevronLeft className="w-5 h-5 text-zinc-400" />
        </button>
        <h2 className="font-bold text-lg text-white">
          {MONTHS_PT[month - 1]} {year}
        </h2>
        <button onClick={nextMonth} className="w-9 h-9 rounded-full bg-zinc-900 border border-white/5 flex items-center justify-center hover:bg-zinc-800 active:scale-90 transition-all">
          <ChevronRight className="w-5 h-5 text-zinc-400" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS_PT.map(d => (
          <div key={d} className="text-center text-xs text-zinc-600 font-bold py-1">{d}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-neon-lime" />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {calendarData?.days?.map((day, i) => {
            if (!day) return <div key={`empty-${i}`} className="aspect-square" />;

            const isToday = day.date === today;
            const hasWorkout = day.workouts_completed > 0;
            const hasPhoto = day.has_photo;

            return (
              <motion.button
                key={day.date}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.01 }}
                onClick={() => hasWorkout || hasPhoto ? setSelectedDay(day) : null}
                className={`aspect-square rounded-xl flex flex-col items-center justify-center relative transition-all ${
                  hasPhoto
                    ? 'bg-neon-lime/20 border-2 border-neon-lime/50'
                    : hasWorkout
                    ? 'bg-neon-purple/15 border border-neon-purple/30'
                    : isToday
                    ? 'bg-zinc-900 border border-zinc-700'
                    : 'bg-zinc-950/50'
                } ${(hasWorkout || hasPhoto) ? 'cursor-pointer active:scale-90' : ''}`}
                data-testid={`calendar-day-${day.date}`}
              >
                <span className={`text-sm font-bold ${
                  hasPhoto ? 'text-neon-lime' : hasWorkout ? 'text-neon-purple' : isToday ? 'text-white' : 'text-zinc-600'
                }`}>
                  {day.day}
                </span>
                {hasPhoto && <Camera className="w-3 h-3 text-neon-lime mt-0.5" />}
                {hasWorkout && !hasPhoto && <Flame className="w-3 h-3 text-neon-purple mt-0.5" />}
              </motion.button>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 justify-center mt-4 text-xs text-zinc-500">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-neon-lime/20 border border-neon-lime/50" />
          <span>Com foto</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-neon-purple/15 border border-neon-purple/30" />
          <span>Treinou</span>
        </div>
      </div>

      {/* Selected Day Detail */}
      <AnimatePresence>
        {selectedDay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50"
            onClick={() => setSelectedDay(null)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              className="bg-zinc-900 border-t border-neon-lime/20 sm:border sm:rounded-2xl rounded-t-2xl p-5 w-full sm:max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-white">
                  {new Date(selectedDay.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}
                </h3>
                <button onClick={() => setSelectedDay(null)} className="text-zinc-500 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex gap-4 mb-4">
                <div className="bg-neon-purple/10 rounded-lg px-3 py-2 flex items-center gap-2">
                  <Dumbbell className="w-4 h-4 text-neon-purple" />
                  <span className="text-sm text-white font-bold">{selectedDay.workouts_completed} treino(s)</span>
                </div>
                {selectedDay.has_photo && (
                  <div className="bg-neon-lime/10 rounded-lg px-3 py-2 flex items-center gap-2">
                    <Camera className="w-4 h-4 text-neon-lime" />
                    <span className="text-sm text-white font-bold">{selectedDay.photos?.length || 0} foto(s)</span>
                  </div>
                )}
              </div>

              {/* Photos */}
              {selectedDay.photos?.length > 0 && (
                <div className="space-y-3">
                  {selectedDay.photos.map(photo => (
                    <div key={photo.id} className="rounded-xl overflow-hidden border border-white/5">
                      <img
                        src={`${API}${photo.photo_url}`}
                        alt="Treino"
                        className="w-full h-64 object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CalendarPage;
